import { useCallback, useEffect, useState } from "react";
import {
  getDestinationCards,
  removeDestinationCardPaymentMethod,
} from "../services/api";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import { useSessionStore } from "../store/useSessionStore";

export interface UseDestinationCardsState {
  cards: ICardPaymentMethod[];
  loading: boolean;
  error: boolean;
  refresh: () => void;
  removeCard: (cardId: string) => Promise<void>;
}

/**
 * Loads enabled destination (push-to-card) instruments for the payout picker.
 */
export const useDestinationCards = (): UseDestinationCardsState => {
  const linkId = useSessionStore((s) => s.session.data?.linkId ?? null);

  const [cards, setCards] = useState<ICardPaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    if (!linkId) {
      setCards([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    getDestinationCards(linkId)
      .then((result) => setCards(result))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [linkId]);

  useEffect(() => {
    load();
  }, [load]);

  const removeCard = useCallback(
    async (cardId: string) => {
      if (!linkId) return;
      setCards((prev) => prev.filter((c) => c.paymentMethodId !== cardId));
      try {
        await removeDestinationCardPaymentMethod(linkId, cardId);
      } finally {
        load();
      }
    },
    [linkId, load]
  );

  return { cards, loading, error, refresh: load, removeCard };
};
